import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { DonutShare } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { KpiCard } from '../../components/KpiCard';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/Table';
import type { VatDetailedProductRow, VatMonthlyRow } from '../../data/fetchers';
import {
  getSettings,
  getVatDetailedReport,
  getVatSummary,
} from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import { fmtAmount, fmtBahtShort } from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';

export function resolveVatMonth(
  requestedMonth: string,
  rows: VatMonthlyRow[],
): string {
  if (rows.some((row) => row.month === requestedMonth)) return requestedMonth;
  return rows.at(-1)?.month ?? '';
}

interface VatMonthlyExportRow {
  month: string;
  exVatBaht: string;
  exVatSatang: number;
  vatBaht: string;
  vatSatang: number;
  grossBaht: string;
  grossSatang: number;
  status: string;
}

interface VatDetailedExportRow {
  productId: string;
  name: string;
  qty: number;
  grossBaht: string;
  grossSatang: number;
  exVatBaht: string;
  exVatSatang: number;
  vatBaht: string;
  vatSatang: number;
}

const exportCols: ExportCol<VatMonthlyExportRow>[] = [
  { key: 'month', header: 'เดือน' },
  { key: 'exVatBaht', header: 'ก่อน VAT' },
  { key: 'exVatSatang', header: 'ก่อน VAT (satang)' },
  { key: 'vatBaht', header: 'VAT' },
  { key: 'vatSatang', header: 'VAT (satang)' },
  { key: 'grossBaht', header: 'รวม' },
  { key: 'grossSatang', header: 'รวม (satang)' },
  { key: 'status', header: 'สถานะ' },
];

const detailedExportCols: ExportCol<VatDetailedExportRow>[] = [
  { key: 'productId', header: 'รหัสสินค้า' },
  { key: 'name', header: 'ชื่อสินค้า' },
  { key: 'qty', header: 'จำนวนที่ขาย' },
  { key: 'grossBaht', header: 'ราคารวม' },
  { key: 'grossSatang', header: 'ราคารวม (satang)' },
  { key: 'exVatBaht', header: 'ก่อน VAT' },
  { key: 'exVatSatang', header: 'ก่อน VAT (satang)' },
  { key: 'vatBaht', header: 'VAT 7%' },
  { key: 'vatSatang', header: 'VAT 7% (satang)' },
];

const detailedColumns: ColumnDef<VatDetailedProductRow, unknown>[] = [
  { accessorKey: 'productId', header: 'รหัสสินค้า' },
  { accessorKey: 'name', header: 'ชื่อสินค้า' },
  {
    accessorKey: 'qty',
    header: 'จำนวนที่ขาย',
    cell: (c) => (
      <span className="num">{c.getValue<number>().toLocaleString()}</span>
    ),
  },
  {
    accessorKey: 'gross',
    header: 'ราคารวม',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
  {
    accessorKey: 'exVat',
    header: 'ก่อน VAT',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
  {
    accessorKey: 'vat',
    header: 'VAT 7%',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
];

export function VatSummaryView() {
  const { scope, ready } = useBranchScope();
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  });
  const vatRate = settings?.vatRate ?? 0.07;

  const [requestedMonth, setRequestedMonth] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: [
      'report',
      'vat',
      scope.branchId,
      scope.allowedBranchIds,
      vatRate,
    ],
    queryFn: () => getVatSummary(scope, vatRate),
    enabled: ready && !!settings,
  });

  const rows = useMemo<VatMonthlyRow[]>(() => data?.rows ?? [], [data]);
  const selectedMonth = resolveVatMonth(requestedMonth, rows);

  const { data: detailedData, isLoading: isDetailLoading } = useQuery({
    queryKey: [
      'report',
      'vat-detail',
      selectedMonth,
      scope.branchId,
      scope.allowedBranchIds,
      vatRate,
    ],
    queryFn: () => getVatDetailedReport(selectedMonth, scope, vatRate),
    enabled: ready && !!settings && !!selectedMonth,
  });

  const donutData = useMemo(
    () =>
      data
        ? [
            { label: 'ก่อน VAT', value: data.exVat },
            { label: 'VAT', value: data.vat },
          ]
        : [],
    [data],
  );

  const exportRows = useMemo<VatMonthlyExportRow[]>(
    () =>
      rows.map((row) => ({
        month: row.month,
        exVatBaht: fmtAmount(row.exVat),
        exVatSatang: row.exVat,
        vatBaht: fmtAmount(row.vat),
        vatSatang: row.vat,
        grossBaht: fmtAmount(row.gross),
        grossSatang: row.gross,
        status: row.status,
      })),
    [rows],
  );

  const columns = useMemo<ColumnDef<VatMonthlyRow, unknown>[]>(
    () => [
      { accessorKey: 'month', header: 'เดือน' },
      {
        accessorKey: 'exVat',
        header: 'ก่อน VAT',
        cell: (c) => (
          <span className="num">{fmtAmount(c.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'vat',
        header: 'VAT',
        cell: (c) => (
          <span className="num">{fmtAmount(c.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'gross',
        header: 'รวม',
        cell: (c) => (
          <span className="num">{fmtAmount(c.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'สถานะ',
        cell: (c) => {
          const val = c.getValue<string>();
          const filed = val === 'ยื่นแล้ว';
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                filed
                  ? 'bg-ok-light text-ok dark:bg-ok/20 dark:text-ok'
                  : 'bg-warn-light text-warn dark:bg-warn/20 dark:text-warn'
              }`}
            >
              {val}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'การจัดการ',
        cell: (c) => {
          const m = c.row.original.month;
          const active = m === selectedMonth;
          return (
            <Btn
              kind={active ? 'primary' : 'default'}
              size="sm"
              onClick={() => {
                setRequestedMonth(m);
                setSearchQuery('');
              }}
            >
              {active ? 'กำลังดูรายละเอียด' : 'ดูรายละเอียด'}
            </Btn>
          );
        },
      },
    ],
    [selectedMonth],
  );

  const filteredDetailedRows = useMemo(() => {
    if (!detailedData?.rows) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return detailedData.rows;
    return detailedData.rows.filter(
      (r) =>
        r.productId.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q),
    );
  }, [detailedData?.rows, searchQuery]);
  const detailedExportRows = useMemo<VatDetailedExportRow[]>(
    () =>
      filteredDetailedRows.map((row) => ({
        productId: row.productId,
        name: row.name,
        qty: row.qty,
        grossBaht: fmtAmount(row.gross),
        grossSatang: row.gross,
        exVatBaht: fmtAmount(row.exVat),
        exVatSatang: row.exVat,
        vatBaht: fmtAmount(row.vat),
        vatSatang: row.vat,
      })),
    [filteredDetailedRows],
  );

  return (
    <div className="bo-fade space-y-6">
      <PageHeader
        title="สรุป VAT"
        subtitle={
          settings
            ? `${settings.shopName} · เลขผู้เสียภาษี ${settings.taxId}`
            : undefined
        }
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="สรุป VAT"
            title="สรุป VAT"
            pdf
          />
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-1">
          <Card title="สัดส่วน VAT ทั้งหมด" className="h-full">
            <Suspense
              fallback={
                <div
                  className="bo-card animate-pulse"
                  style={{ height: CHART_H.md }}
                />
              }
            >
              <DonutShare data={donutData} valueFormat={fmtBahtShort} />
            </Suspense>
          </Card>
        </div>

        <div className="lg:col-span-2">
          {!ready || !settings || isLoading ? (
            <div className="bo-card h-64 animate-pulse" />
          ) : (
            <DataTable columns={columns} data={rows} />
          )}
        </div>
      </div>

      <div className="border-t border-line pt-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-ink flex items-center gap-2">
              <span>รายละเอียดภาษีมูลค่าเพิ่ม 7%</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-light text-primary">
                ประจำเดือน {selectedMonth}
              </span>
            </h2>
            <p className="text-sm text-ink-3 mt-1">
              รายการสินค้าและภาษีมูลค่าเพิ่มที่คำนวณจริงจากยอดขายประจำเดือน{' '}
              {selectedMonth}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="ค้นหาสินค้า (ชื่อ/รหัส)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-btn border border-line bg-surface text-ink placeholder:text-ink-3 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 w-full md:w-64"
            />
            {detailedData && (
              <ExportMenu
                rows={detailedExportRows}
                cols={detailedExportCols}
                filename={`รายละเอียด VAT 7% - เดือน ${selectedMonth}`}
                title={`รายละเอียด VAT 7% - เดือน ${selectedMonth}`}
                pdf
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard
            label="ยอดขายรวม (Gross)"
            value={detailedData ? fmtAmount(detailedData.gross) : '—'}
            unit="บาท"
            tone="primary"
          />
          <KpiCard
            label="ยอดก่อนภาษี (Ex-VAT)"
            value={detailedData ? fmtAmount(detailedData.exVat) : '—'}
            unit="บาท"
            tone="accent"
          />
          <KpiCard
            label="ภาษีมูลค่าเพิ่ม (VAT 7%)"
            value={detailedData ? fmtAmount(detailedData.vat) : '—'}
            unit="บาท"
            tone="ok"
          />
          <KpiCard
            label="จำนวนสินค้าที่มีขาย"
            value={detailedData ? detailedData.rows.length.toString() : '—'}
            unit="รายการ"
            tone="warn"
          />
        </div>

        {!ready || !settings || isDetailLoading ? (
          <div className="bo-card h-40 animate-pulse" />
        ) : (
          <DataTable
            columns={detailedColumns}
            data={filteredDetailedRows}
            pageSize={10}
            emptyMessage="ไม่พบข้อมูลสินค้าสำหรับเดือนนี้ หรือคำค้นหาไม่ตรงกับสินค้าใดเลย"
          />
        )}
      </div>
    </div>
  );
}
